import type { RegionScan } from '@lizrd/contracts';
import { Pill } from './ui/pill';
import { Alert } from './ui/alert';
import { Badge } from './ui/badge';

/**
 * Shows which cloud regions/locations a sync run covered, and warns when the scan
 * was truncated (more regions than the per-run cap — the rare AWS case). One
 * component so every sync-progress surface renders regions consistently.
 *
 * - `full`: a labeled row + region chips + a truncation warning banner (detail modal).
 * - `inline`: a compact "· N regions" note (with the full list on hover) plus a
 *   warning badge when truncated (tiles, rows, the live pipeline).
 */
export function RegionsScanned({
  regions,
  variant = 'inline',
  className,
}: {
  regions: RegionScan | null | undefined;
  variant?: 'full' | 'inline';
  className?: string;
}) {
  if (!regions || regions.scanned.length === 0) return null;
  const { scanned, total, truncated } = regions;
  const label = `${scanned.length} region${scanned.length === 1 ? '' : 's'}`;
  const list = scanned.join(', ');

  if (variant === 'full') {
    return (
      <div className={className}>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted">Regions scanned</span>
          <span className="text-bone">{truncated ? `${scanned.length} of ${total}` : label}</span>
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {scanned.map((r) => (
            <Pill key={r}>{r}</Pill>
          ))}
        </div>
        {truncated && (
          <Alert variant="warning" className="mt-2">
            Only {scanned.length} of {total} regions were scanned this run —{' '}
            {total - scanned.length} not covered. Resources in the uncovered regions aren’t updated
            this run and won’t generate recommendations until they’re scanned.
          </Alert>
        )}
      </div>
    );
  }

  return (
    <span className={className} title={list}>
      · {label}
      {truncated && (
        <Badge
          variant="warning"
          className="ml-1.5"
          title={`${total - scanned.length} region(s) not scanned`}
        >
          {scanned.length}/{total}
        </Badge>
      )}
    </span>
  );
}
